import { useState } from "react";
import { Button } from "~/atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/atoms/dropdown-menu";
import { ChevronRight, History } from "lucide-react";
import { type DocumentDetailsRelease } from "~/api/types";
import { StatusBadge } from "~/molecules/StatusBadge";
import { formatDate } from "~/helpers/date";
import { Link, useSearchParams } from "react-router";
import { Badge } from "~/atoms/badge";
import { DocumentArtifactStatus } from "~/config/types";
import { useTranslation } from "react-i18next";
import i18n from "~/i18n";
import { cn } from "~/lib/utils";

interface Props {
  documentId: number;
  selectedReleaseId: number;
  releases: DocumentDetailsRelease[];
  onReleaseChange: () => void;
}

const getStatusDate = (release: DocumentDetailsRelease): string | null => {
  switch (release.artifact.status) {
    case DocumentArtifactStatus.published:
      return release.artifact.publishedAt;
    case DocumentArtifactStatus.archived:
      return release.artifact.archivedAt;
    default:
      return release.createdAt;
  }
};

const getStatusText = (release: DocumentDetailsRelease): string => {
  const date = getStatusDate(release);
  if (!date) return "";

  switch (release.artifact.status) {
    case DocumentArtifactStatus.published:
      return `${i18n.t(($) => $.commons.workflow.published)} ${formatDate(date)}`;
    case DocumentArtifactStatus.archived:
      return `${i18n.t(($) => $.commons.workflow.archived)} ${formatDate(date)}`;
    default:
      return `${i18n.t(($) => $.commons.workflow.created)} ${formatDate(date)}`;
  }
};

const ReleaseStatusBadge = ({
  status,
  isWorkflowOngoing,
}: {
  status?: DocumentArtifactStatus;
  isWorkflowOngoing?: boolean;
}) => {
  return (
    <div className="flex gap-2">
      {status === DocumentArtifactStatus.draft &&
        isWorkflowOngoing === false && (
          <div className="bg-card rounded-full">
            <Badge variant="warn">
              {i18n.t(($) => $.commons.workflow.notStarted)}
            </Badge>
          </div>
        )}
      {status && (
        <div className="bg-card rounded-full">
          <StatusBadge.DocumentArtifactStatus status={status} />
        </div>
      )}
    </div>
  );
};

export function ReleaseSelect({
  documentId,
  selectedReleaseId,
  releases,
}: Props) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [isOpen, setIsOpen] = useState(false);

  const sortedReleases = [...releases].sort(
    (a, b) => b.majorVersion - a.majorVersion,
  );

  const selectedRelease = sortedReleases.find(
    (release) => release.id === selectedReleaseId,
  );

  const getReleaseUrl = (releaseId: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("releaseId", String(releaseId));
    return `/documents/${documentId}?${next.toString()}`;
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <History className="size-4" />
          {t(($) => $.commons.workflow.versionOrder, {
            order: selectedRelease?.majorVersion ?? 0,
          })}
          <ReleaseStatusBadge
            status={selectedRelease?.artifact.status}
            isWorkflowOngoing={selectedRelease?.artifact.isWorkflowOngoing}
          />
          <ChevronRight
            className={cn("size-4 transition-transform", isOpen && "rotate-90")}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-card w-87.5" align="end">
        <DropdownMenuLabel>
          {t(($) => $.commons.workflow.versions)}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {sortedReleases.map((release) => (
            <DropdownMenuItem
              key={release.id}
              className={release.id === selectedReleaseId ? "bg-secondary" : ""}
              asChild
            >
              <Link
                to={getReleaseUrl(release.id)}
                className="flex w-full flex-col gap-1"
              >
                <div className="flex w-full items-center justify-between">
                  <span className="font-medium">
                    {t(($) => $.commons.workflow.versionOrder, {
                      order: release.majorVersion,
                    })}
                  </span>
                  <ReleaseStatusBadge
                    status={release.artifact.status}
                    isWorkflowOngoing={release.artifact.isWorkflowOngoing}
                  />
                </div>
                <div className="text-muted-foreground flex w-full justify-between text-xs">
                  <span>{release.artifact.title}</span>
                  <span className="whitespace-nowrap">
                    {getStatusText(release)}
                  </span>
                </div>
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
